import { DEFAULT_LOCALE } from '@constants/locales';
import { QUERY_KEY } from '@constants/queries';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import { fetchPublicConfiguration } from '@hooks/queries/useFetchPublicConfiguration';
import {
  fetchPublicSetting,
  useFetchPublicSetting,
} from '@hooks/queries/useFetchPublicSetting';
import { fetchSEO, useFetchSEO } from '@hooks/queries/useFetchSEO';
import { Flex, Title } from '@mantine/core';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import { EditorJsRender } from '@organisms/EditorJsRender';
import { SEO } from '@organisms/SEO';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  const queryClient = new QueryClient();

  const settingsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_SETTING, 'PRIVACY'],
    queryFn: async () => {
      return await fetchPublicSetting('PRIVACY');
    },
  });

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  const seoPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_SEO],
    queryFn: async () => {
      return await fetchSEO();
    },
  });

  await Promise.all([
    categoriesPromise,
    settingsPromise,
    configurationsPromise,
    seoPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? DEFAULT_LOCALE, [
        'common',
        'privacy',
      ])),
      dehydratedState: dehydrate(queryClient),
    },
  };
};

const ContactsPublicPage = () => {
  const { t } = useTranslation('privacy');

  const {
    data: privacy,
    isFetching,
    isFetched,
  } = useFetchPublicSetting('PRIVACY');

  const { data: dataSEO, isLoading: isLoadingSEO } = useFetchSEO();

  const SEOPrivacy = dataSEO?.value.find((page) => page.name === 'privacy');

  if ((isFetching && !privacy) || isLoadingSEO) {
    return <LoadingStateComponent />;
  }

  return (
    <>
      <SEO
        title={SEOPrivacy?.title}
        keywords={SEOPrivacy?.keywords}
        description={SEOPrivacy?.description}
      />
      {isFetched && !privacy ? (
        <EmptyStateComponent />
      ) : (
        <Flex direction="column" gap={8}>
          <Title variant="h1Orange">{t('privacy')}</Title>
          <Flex direction="column">
            {privacy?.value && <EditorJsRender data={privacy.value} />}
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default ContactsPublicPage;
